import { type FC, useRef, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import LazyLoad from 'dibs-react-lazy-image';
import SocialDropdown from 'dibs-interiors-photos/exports/SocialDropdown.ts';
import PhotoHeart from '../LikingPhotos/PhotoHeart.tsx';
import PhotoOverlayItem from '../PhotoOverlayItem.tsx';
import { trackPhotoClick, trackSavePhoto } from '../helpers/trackingHelpers.ts';
import { getSrcsetUrl, getSrcsetString } from '../helpers/srcSet.ts';

import styles from './RelatedPhotosGridTile.scss';

import { type RelatedPhotosGridTile_photo$key } from './__generated__/RelatedPhotosGridTile_photo.graphql.ts';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';

const LAZY_LOAD_OFFSET = 500;
const IMAGE_WIDTH = 400;
const IMG_SIZES = '(max-width: 568px) 50vw, 25vw';

type Props = {
    isMobile: boolean;
    photo: RelatedPhotosGridTile_photo$key;
};

const RelatedPhotosGridTile: FC<Props> = props => {
    const { photo: photoRef, isMobile } = props;

    const photo = useFragment(
        graphql`
            fragment RelatedPhotosGridTile_photo on PhotosSearchBrowsePhoto {
                id
                serviceId
                url
                name
                path
                width
                height
                photoLike {
                    photoLikeId(userId: $userId) @include(if: $hasUserId)
                    totalPhotoLikes
                }
            }
        `,
        photoRef
    );

    const photoPath = photo?.path || '';
    const photoWidth = photo?.width || 0;
    const photoHeight = photo?.height || 0;
    const aspectRatio = (photoHeight * 100) / photoWidth;
    const photoUrl = photo?.url || '';

    const photoNodeId = photo?.id;
    const photoId = photo?.serviceId || '';
    const photoLike = photo?.photoLike;
    const photoLikeId = photoLike?.photoLikeId;
    const totalPhotoLikes = photoLike?.totalPhotoLikes || 0;

    const tileRef = useRef<HTMLDivElement>(null);

    const onPhotoLikeAdded = useCallback(() => trackSavePhoto({ label: 'photos' }), []);

    const handleClick = (): void => {
        trackPhotoClick({ label: 'photo' });
        window.location.assign(handleLocaleUrl(photoUrl, GLOBAL_CLIENT_ONLY_LOCALE));
    };

    return (
        <div
            onClick={handleClick}
            onKeyDown={e => {
                if (e.key === 'Enter' || e.key === 'Space') {
                    handleClick();
                }
            }}
            role="button"
            tabIndex={0}
            className={styles.link}
        >
            <div className={styles.tile} style={{ paddingTop: `${aspectRatio}%` }} ref={tileRef}>
                <LazyLoad offsetVertical={LAZY_LOAD_OFFSET} visibilityRef={tileRef}>
                    <img
                        className={styles.image}
                        src={getSrcsetUrl(photoPath, IMAGE_WIDTH)}
                        srcSet={getSrcsetString(photoPath)}
                        sizes={IMG_SIZES}
                        alt={photo?.name || ''}
                    />
                </LazyLoad>
            </div>
            {isMobile ? (
                <div className={styles.socialDropdownWrapper}>
                    <PhotoOverlayItem>
                        <SocialDropdown
                            photoNodeId={photoNodeId}
                            photoId={photoId}
                            photoLikeId={photoLikeId}
                            onPhotoLikeAdded={onPhotoLikeAdded}
                            photoUrl={photoUrl}
                        />
                    </PhotoOverlayItem>
                </div>
            ) : (
                <div className={styles.photoHeartWrapper}>
                    <PhotoOverlayItem>
                        <PhotoHeart
                            photoNodeId={photoNodeId}
                            photoId={photoId}
                            photoLikeId={photoLikeId}
                            onPhotoLikeAdded={onPhotoLikeAdded}
                            totalPhotoLikes={totalPhotoLikes}
                        />
                    </PhotoOverlayItem>
                </div>
            )}
        </div>
    );
};

export default RelatedPhotosGridTile;
